import React from "react";
import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

// ═══════════════════════════════════════════════════════════════════════════
// FEC API SERVICE LAYER
// ═══════════════════════════════════════════════════════════════════════════
// Free API at https://api.open.fec.gov/v1/
// Get your key: https://api.open.fec.gov/developers/
// Rate limit: 1,000 requests/hour per key
// ═══════════════════════════════════════════════════════════════════════════

const FEC_BASE = "https://api.open.fec.gov/v1";
const apiCache = {};

async function fecFetch(path, params, apiKey) {
  const queryStr = new URLSearchParams({ api_key: apiKey, ...params }).toString();
  const url = `${FEC_BASE}${path}?${queryStr}`;
  if (apiCache[url]) return apiCache[url];
  const res = await fetch(url);
  if (!res.ok) {
    if (res.status === 429) throw new Error("FEC rate limit reached. Try again in a few minutes.");
    if (res.status === 403) throw new Error("Invalid FEC API key.");
    throw new Error(`FEC API error: ${res.status}`);
  }
  const data = await res.json();
  apiCache[url] = data;
  return data;
}

// Known candidates who have withdrawn but may still appear in FEC data.
// Format: FEC candidate ID or "LASTNAME" as fallback match.
const WITHDRAWN_CANDIDATES = new Set([
  "S6TX00861",   // Colin Allred — dropped out Dec 8, 2025
  "ALLRED",      // fallback name match
]);

async function fetchFECCandidates(apiKey, state, district, office, year = 2026) {
  const params = { state, office, election_year: year, sort: "name", per_page: 40, candidate_status: "C" };
  if (office === "H" && district) params.district = String(district).padStart(2, "0");
  let results = (await fecFetch("/candidates/search/", params, apiKey)).results || [];
  // If no active candidates found, try without the status filter
  if (!results.length) {
    delete params.candidate_status;
    results = (await fecFetch("/candidates/search/", params, apiKey)).results || [];
  }
  // Filter out candidates marked as inactive
  results = results.filter(c => !c.candidate_inactive);
  // Filter out known withdrawn candidates
  results = results.filter(c => {
    if (WITHDRAWN_CANDIDATES.has(c.candidate_id)) return false;
    const last = (c.name || "").split(",")[0].trim().toUpperCase();
    if (WITHDRAWN_CANDIDATES.has(last)) return false;
    return true;
  });
  console.log(`[FEC] ${results.length} active candidates for ${state}-${office}${district ? district : ""} (${year})`);
  return results;
}

async function fetchCandidateTotals(apiKey, candidateId, year = 2026) {
  let result = null;
  // Strategy 1: cycle parameter (correct per FEC docs) — no sort, no trailing slash
  try {
    const data = await fecFetch(`/candidate/${candidateId}/totals`, { cycle: year, per_page: 1 }, apiKey);
    result = data.results?.[0] || null;
  } catch (e) { /* try next */ }
  // Strategy 2: full_election=true with cycle
  if (!result) {
    try {
      const data = await fecFetch(`/candidate/${candidateId}/totals`, { cycle: year, full_election: true, per_page: 1 }, apiKey);
      result = data.results?.[0] || null;
    } catch (e) { /* try next */ }
  }
  // Strategy 3: no filter (get whatever is available)
  if (!result) {
    try {
      const data = await fecFetch(`/candidate/${candidateId}/totals`, { per_page: 1 }, apiKey);
      result = data.results?.[0] || null;
    } catch (e) { /* all failed */ }
  }
  if (result) {
    console.log(`[Finance] ✓ ${candidateId}: receipts=${result.receipts || result.total_receipts || 0}, spent=${result.disbursements || result.total_disbursements || 0}, cash=${result.cash_on_hand_end_period || result.last_cash_on_hand_end_period || 0}`);
  } else {
    console.log(`[Finance] ✕ No totals for ${candidateId}`);
  }
  return result;
}

// Also fetch top contributors (Schedule A itemized receipts)
async function fetchTopDonors(apiKey, committeeId, year = 2026) {
  if (!committeeId) return [];
  try {
    const data = await fecFetch("/schedules/schedule_a/by_contributor/", {
      committee_id: committeeId, cycle: year, per_page: 10, sort: "-total"
    }, apiKey);
    return (data.results || []).map(d => ({
      name: d.contributor_name || "Unknown",
      total: d.total || 0,
      type: d.contributor_type || "",
    }));
  } catch (err) { return []; }
}

const SRC_COLORS = { small: "#4ECDC4", large: "#45B7D1", pac: "#F7DC6F", self: "#E8A87C", xfer: "#C38D9E", other: "#85CDCA" };

function transformFinancials(t) {
  if (!t) return { totalFunding: 0, fundingSources: [], cashOnHand: 0, totalSpent: 0, debt: 0, coverageDates: null };
  const total = t.receipts || t.total_receipts || 0;
  const spent = t.disbursements || t.total_disbursements || 0;
  const cash = t.cash_on_hand_end_period || t.last_cash_on_hand_end_period || 0;
  const debt = t.debts_owed_by_committee || t.debt_owed_by_committee || 0;
  const coverageStart = t.coverage_start_date || null;
  const coverageEnd = t.coverage_end_date || null;
  const vals = [
    ["Small Donors (<$200)", t.individual_unitemized_contributions || 0, SRC_COLORS.small],
    ["Large Donors ($200+)", t.individual_itemized_contributions || 0, SRC_COLORS.large],
    ["PACs", t.other_political_committee_contributions || 0, SRC_COLORS.pac],
    ["Self-Funded", (t.candidate_contribution || 0) + (t.candidate_loans || 0), SRC_COLORS.self],
    ["Transfers", t.transfers_from_other_authorized_committees || 0, SRC_COLORS.xfer],
  ];
  const known = vals.reduce((s, [, v]) => s + v, 0);
  vals.push(["Other", Math.max(0, total - known), SRC_COLORS.other]);
  const sources = vals.filter(([, v]) => v > 0 && total > 0).map(([name, amount, color]) => ({
    name, amount, color, percentage: Math.round((amount / total) * 100)
  }));
  const pctSum = sources.reduce((s, x) => s + x.percentage, 0);
  if (pctSum > 0 && pctSum !== 100 && sources.length) sources[sources.length - 1].percentage += (100 - pctSum);
  return {
    totalFunding: total, fundingSources: sources,
    cashOnHand: cash, totalSpent: spent, debt,
    coverageDates: coverageStart && coverageEnd ? { start: coverageStart, end: coverageEnd } : null,
  };
}

function cleanFECName(raw) {
  if (!raw) return "Unknown";
  // FEC stores names as "SMITH, JOHN A III" — convert to "John A Smith III"
  const [lastName, ...rest] = raw.split(", ");
  const firstName = rest.join(", ");
  const toTitle = s => s.toLowerCase().replace(/\b\w/g, ch => ch.toUpperCase());
  const suffixes = ["II", "III", "IV", "JR", "JR.", "SR", "SR."];
  const parts = `${firstName} ${lastName}`.trim().split(/\s+/).map(p =>
    suffixes.includes(p.toUpperCase()) ? p.toUpperCase() : toTitle(p)
  );
  return parts.filter(Boolean).join(" ");
}

// ═══════════════════════════════════════════════════════════════════════════
// BRIDGE SCORE DATA
// ═══════════════════════════════════════════════════════════════════════════
// Manually curated bridge scores based on candidate rhetoric analysis.
// Score: 1=Very Dividing, 2=Dividing, 3=Neutral, 4=Bridging, 5=Very Bridging
// Key: lowercase last name → { score, explanation }
// ═══════════════════════════════════════════════════════════════════════════

const BRIDGE_SCORES = {
  // ── TX Senate 2026 ──
  "paxton": {
    score: 1,
    explanation: `Ken Paxton's rhetoric is built almost entirely around combat and destruction metaphors. His campaign website describes him as taking "a sledgehammer to the D.C. Establishment" and urges supporters to "strike back at the Swamp." He frames politics as warfare between MAGA loyalists and "the establishment," calling fellow Republican Cornyn a "Republican In Name Only." He has tapped into Islamophobic sentiment in campaign advertisements, targeting an Islamic community development through lawsuits. His entire brand is built on defiance, confrontation, and an us-vs-them framing that leaves little room for bridge-building.`
  },
  "crockett": {
    score: 2,
    explanation: `Jasmine Crockett's brand is explicitly combative. At her campaign launch, she told supporters: "Trump, I know you're watching... You're not entitled to a damn thing in Texas. I'm coming for you." She has described her approach as doing "the edgy things, the things that the political consultants will never tell you to do." Her most viral moments are confrontational exchanges with Republican colleagues, including a famous personal insult directed at Rep. Marjorie Taylor Greene that was viewed over 20 million times. CNN described her as betting "that her brand of combative, progressive politics could win over Texas voters." She rates slightly higher than Paxton because her confrontational style is aimed at defending democratic norms rather than attacking fellow party members, but her rhetoric is primarily designed to energize her base through opposition.`
  },
  "cornyn": {
    score: 3,
    explanation: `Cornyn presents the most mixed picture. He has a genuine bipartisan legislative record — the Lugar Center ranked him fifth among senators for bipartisanship in 2023, and he played a leading role in the bipartisan gun safety bill after the Uvalde school massacre. He has framed his current campaign as "a positive campaign about the facts and the results." However, in the current primary, his rhetoric has turned sharply negative, calling Paxton a "corrupt attorney general" and an "albatross" who would cause "an election day massacre." He has also participated in Islamophobic campaign tactics targeting the EPIC City development. His rhetoric swings between institutional sobriety and primary-mode attacks, landing him squarely in neutral territory — someone with bridging instincts who is currently deploying divisive tactics to survive.`
  },
  "talarico": {
    score: 4,
    explanation: `Talarico's campaign is the most explicitly oriented toward bridging. His most-watched TikTok, viewed 15.5 million times, argues that "the biggest division in our politics is not left versus right, it's top versus bottom." CNN reported he has "centered much of his strategy around finding common ground with voters, including independents and Republicans," telling supporters "We're going everywhere. We're talking to everyone. We're not writing off any voter." He compared his campaign's message to unity, saying "joy and unity and love are so much more powerful than hate and fear and division." He leads with his Christian faith and quotes scripture — a deliberate bridge to conservative voters. He doesn't earn a 5 because his rhetoric still includes combative populist language against billionaires and "the establishment in both political parties," and an intra-party controversy over an alleged disparaging remark introduced a divisive note.`
  },
  // ── PA-03 House 2026 ──
  "street": {
    score: 3,
    explanation: `Sharif Street mixes populist anti-Republican framing with community-oriented messaging. His campaign website describes him as "a lifelong Philadelphian, public servant, and fighter for justice" bringing "bold, thoughtful leadership" to Washington. He says "people are getting screwed by Republicans in Washington, who are more focused on helping their billionaire friends." At forums, he used inflammatory language, saying ICE "has recruited people from neo-Nazis" and calling the agency "rotten to its core." But he also struck a more measured tone elsewhere, saying Trump is "out of control, but there are things we can do" and citing lawsuits he participated in as constructive action. His rhetoric balances combative and pragmatic elements, landing him in neutral territory.`
  },
  "cephas": {
    score: 3,
    explanation: `Morgan Cephas has been described as "a bridge between local government and state leadership." At forums she focused on practical policy work — affordable housing, maternal health, and extending ACA tax credits, saying "We want to be sure Philadelphians can afford Philadelphia." She grounds her rhetoric in community service and constituent representation, saying "my policy perspective in delivering is based on the constituents that I represent." However, she also called Trump "the distractor-in-chief" who "has gone past being unconstitutional" and should be impeached. Like Street, she straddles the line between pragmatism and combativeness, yielding a neutral score.`
  },
  "rabb": {
    score: 2,
    explanation: `Chris Rabb's rhetoric is the most confrontational in the PA-03 field. At candidate forums, he repeatedly attacked "billionaires who support Democrats" and returned to anti-corporate populist themes throughout. He called for "not just abolishing ICE, but also dismantling all immigrant detention centers." On Trump, he said "constitutionality clearly does not matter to the people in power" and made a mocking reference to the president's eating habits. He framed the housing crisis as driven by greed connected to political donations, asking "are we going to be part of those folks who have the political will and vision and care to do what's right?" His rhetoric consistently frames politics as an adversarial struggle against both corporations and political opponents, with little bridge-building language.`
  },
  "stanford": {
    score: 4,
    explanation: `Ala Stanford's rhetoric is the most community- and service-oriented in the PA-03 race. She said "I didn't come to this campaign from a career in politics — I came here from a career in caring for others." She emphasized her COVID response work: "When COVID hit and testing was scarce, I didn't wait for someone else to solve it. We stepped up, built clinics and made sure people were cared for." Her campaign announcement focused on action-oriented service: "when I see a problem in our city, I take action to fix it." While she does criticize the Trump administration, she frames opposition in terms of protecting communities rather than attacking opponents. She doesn't earn a 5 because she does engage in partisan framing and called for abolishing ICE at a candidate forum, but her overall orientation is toward community healing and service.`
  },
  "oxman": {
    score: 3,
    explanation: `Dave Oxman leads with empathy and professional experience, speaking at forums about meeting "people on some of the very worst days of their lives" and his understanding that health extends beyond medicine. He says "America has the greatest healthcare in the world, but what does it matter when so many Americans can't access it?" — framing the system as broken rather than blaming specific people. However, he also described the Trump administration as a "daily dumpster fire" and called ICE actions "performative acts of cruelty." He called for a national physician walkout to "fight the Trump administration's war on science and health." He blends a compassionate personal narrative with pointed anti-Trump rhetoric, landing him in neutral territory.`
  },
  // ── PA-07 House 2026 ──
  "mackenzie": {
    score: 4,
    explanation: `Ryan Mackenzie's rhetoric and legislative record are notably bipartisan for a Republican in a swing district. He has introduced multiple bills with Democratic co-sponsors, including veteran care and military family support legislation. He urged Congress to collaborate on DHS funding, saying "shutdowns are the wrong way to settle differences." He bucked his own party leadership by signing a discharge petition to force a vote on extending ACA subsidies, calling it "a bipartisan commonsense proposal." He acknowledged representing "all of those diverse interests" in his evenly divided district. He doesn't earn a 5 because he voted for the Big Beautiful Bill using party-line framing about "wide-open borders" and "reckless spending," called his Democratic critics "liberal extremists," and attacked Lamont McClure over ICE enforcement in a House floor speech. But his overall orientation is toward bipartisan governance and pragmatic coalition-building.`
  },
  "brooks": {
    score: 2,
    explanation: `Bob Brooks launched his campaign with combative rhetoric: "I'm running for Congress because Washington doesn't give a damn about working people, and it's time for that to change." He accused incumbent Mackenzie of never having "held a real job a day in his life" and said Mackenzie "sells out Pennsylvania working families every chance he gets." His campaign website frames politics in adversarial terms: "Washington, D.C. is on fire, and working people keep getting burned." He dismissed his primary opponents as career politicians who only "talk about" working-class families. His endorsement from Bernie Sanders aligns with an anti-corporate, anti-establishment message. While his working-class firefighter narrative is authentic and relatable, his rhetoric is consistently adversarial — attacking both Republicans and fellow Democrats — with little bridge-building language.`
  },
  "obando-derstine": {
    score: 3,
    explanation: `Carol Obando-Derstine presents a mixed rhetorical profile. At her campaign launch, she opened with "Frankly, I'm pissed" and attacked Mackenzie as "like a bobblehead doll, doing nothing, saying nothing." Her campaign frames itself as "fighting back, standing up for our values." However, former Rep. Susan Wild called her "a natural born bridge builder," and Obando-Derstine herself emphasized coalition-building: "I am in the best position to build that broad coalition because I understand the district and the struggles that working families face." Her immigrant family story — coming to the U.S. from Colombia at age 3 — and community-service background (food pantry, after-school programs, energy transition work) suggest a bridging orientation that her combative campaign rhetoric sometimes undercuts. She straddles the line between populist anger and pragmatic community focus.`
  },
  "mcclure": {
    score: 3,
    explanation: `Lamont McClure mixes governance-focused pragmatism with combative primary rhetoric. He told supporters "We must continue to fight... the fight must go to Washington because I am for working people." He grounded his candidacy in family service values: "my parents taught me that whatever God-given talent I have, I have an obligation to use it in service of others." He touts a record of executive governance — supporting 1,600 small businesses through the pandemic and cutting property taxes. However, he attacked primary opponent Crosswell as anti-union and "a latecomer to the Democratic Party," and after Mackenzie's Big Beautiful Bill vote he said "I'm glad he can't pretend to be a moderate anymore." His rhetoric mixes practical bipartisan governance with sharp attacks on both the Republican incumbent and his primary rivals, yielding a neutral score.`
  },
  "pinsley": {
    score: 2,
    explanation: `Mark Pinsley's rhetoric is among the most confrontational in the PA-07 field. He framed his campaign around confrontation: "A small crowd of billionaires and bought politicians quite frankly think our country belongs to them. I believe it belongs to the people, and I believe that we need to fight for that." He took aim at both Republicans and his own party's establishment, saying "Pennsylvanians are tired of false promises, excuses, and candidates who are ashamed to be Democrats. Running establishment candidates who share the same talking points is not the answer." He staked a claim to the left wing of the field, calling for Medicare for All, a wealth tax, and ending Citizens United. While he has been an active and bold county controller (leading Tesla pension divestment, protecting mail-in ballot drop boxes), his rhetoric consistently frames politics as a fight against both corporate power and establishment Democrats, with little bridge-building language.`
  }
};

function lookupBridgeScore(name) {
  if (!name) return null;
  const lower = name.toLowerCase();
  for (const [key, val] of Object.entries(BRIDGE_SCORES)) {
    if (lower.includes(key)) return val;
  }
  return null;
}

function transformFECCandidate(c, financials, districtId, i) {
  const display = cleanFECName(c.name);
  const nameParts = display.split(" ");
  const first = nameParts[0] || "Unknown";
  const last = nameParts.length > 1 ? nameParts[nameParts.length - 1] : first;
  const { totalFunding, fundingSources, cashOnHand, totalSpent, debt, coverageDates } = transformFinancials(financials);
  const incYears = c.incumbent_challenge === "I" ? Math.max(1, (c.active_through || 2026) - (c.first_file_date ? new Date(c.first_file_date).getFullYear() : 2024)) : 0;
  return {
    id: c.candidate_id || `${districtId}-${i}`, fecId: c.candidate_id,
    committeeId: c.principal_committees?.[0]?.committee_id || null,
    party: c.party_full || c.party || "",
    name: display, firstName: first || last, lastName: last,
    avatarSeed: display.split('').reduce((a, ch) => a + ch.charCodeAt(0), 0),
    bridgeScore: null, bridgeExplanation: null, totalFunding, fundingSources, cashOnHand, totalSpent, debt, coverageDates,
    isIncumbent: c.incumbent_challenge === "I", yearsHeld: incYears,
    dataSource: "fec",
  };
}

// ═══════════════════════════════════════════════════════════════════════════
// POLYMARKET PREDICTION MARKET DATA (for candidate ranking only)
// ═══════════════════════════════════════════════════════════════════════════
// Uses the public Gamma API to find prediction market data for Senate races.
// This data is NOT displayed — it's only used to help rank candidates.
// ═══════════════════════════════════════════════════════════════════════════

const polymarketCache = {};

async function fetchPolymarketOdds(state, office) {
  if (office !== "S") return {}; // Only Senate races have Polymarket markets
  const cacheKey = `${state}-${office}`;
  if (polymarketCache[cacheKey] !== undefined) return polymarketCache[cacheKey];
  try {
    // Search for senate race markets by state name
    // Build state name from abbreviation for search
    const stateNames = {AL:"Alabama",AK:"Alaska",AZ:"Arizona",AR:"Arkansas",CA:"California",CO:"Colorado",CT:"Connecticut",DE:"Delaware",FL:"Florida",GA:"Georgia",HI:"Hawaii",ID:"Idaho",IL:"Illinois",IN:"Indiana",IA:"Iowa",KS:"Kansas",KY:"Kentucky",LA:"Louisiana",ME:"Maine",MD:"Maryland",MA:"Massachusetts",MI:"Michigan",MN:"Minnesota",MS:"Mississippi",MO:"Missouri",MT:"Montana",NE:"Nebraska",NV:"Nevada",NH:"New Hampshire",NJ:"New Jersey",NM:"New Mexico",NY:"New York",NC:"North Carolina",ND:"North Dakota",OH:"Ohio",OK:"Oklahoma",OR:"Oregon",PA:"Pennsylvania",RI:"Rhode Island",SC:"South Carolina",SD:"South Dakota",TN:"Tennessee",TX:"Texas",UT:"Utah",VT:"Vermont",VA:"Virginia",WA:"Washington",WV:"West Virginia",WI:"Wisconsin",WY:"Wyoming"};
    const stateName = stateNames[state] || state;
    const url = `https://gamma-api.polymarket.com/events?closed=false&limit=10&tag=senate-elections`;
    const res = await fetch(url);
    if (!res.ok) throw new Error(`Polymarket: ${res.status}`);
    const events = await res.json();
    // Find event matching this state
    const stateEvent = events.find(e =>
      (e.title || "").toLowerCase().includes(stateName.toLowerCase()) ||
      (e.slug || "").toLowerCase().includes(state.toLowerCase())
    );
    if (!stateEvent || !stateEvent.markets) {
      console.log(`[Polymarket] No market found for ${state} Senate`);
      polymarketCache[cacheKey] = {};
      return {};
    }
    // Extract candidate names and their market-implied probabilities
    const odds = {};
    for (const m of stateEvent.markets) {
      const title = (m.question || m.groupItemTitle || "").toLowerCase();
      const prices = JSON.parse(m.outcomePrices || "[]");
      const yesPrice = parseFloat(prices[0]) || 0;
      // Try to extract candidate name from market title
      const outcomes = JSON.parse(m.outcomes || "[]");
      if (outcomes[0]) {
        odds[outcomes[0].toLowerCase()] = yesPrice;
      }
      // Also try matching from title
      if (title && yesPrice > 0) {
        odds[title] = yesPrice;
      }
    }
    console.log(`[Polymarket] Found odds for ${state} Senate:`, odds);
    polymarketCache[cacheKey] = odds;
    return odds;
  } catch (err) {
    console.log(`[Polymarket] Could not fetch for ${state}:`, err.message);
    polymarketCache[cacheKey] = {};
    return {};
  }
}

// Rank candidates by noteworthiness and return top 5
function rankAndFilterCandidates(candidates, polymarketOdds) {
  // Score each candidate
  const scored = candidates.map(c => {
    let score = 0;
    // Factor 1: Incumbent status (major boost)
    if (c.isIncumbent) score += 1000000;
    // Factor 2: Total money raised (primary signal)
    score += (c.totalFunding || 0);
    // Factor 3: Polymarket odds (if available, huge signal)
    if (polymarketOdds && Object.keys(polymarketOdds).length > 0) {
      const lastName = (c.lastName || "").toLowerCase();
      const fullName = (c.name || "").toLowerCase();
      for (const [key, prob] of Object.entries(polymarketOdds)) {
        if (key.includes(lastName) || lastName.includes(key) || key.includes(fullName)) {
          // Polymarket probability as a massive boost (0-1 scale → 0-10M boost)
          score += prob * 10000000;
          break;
        }
      }
    }
    return { ...c, _rankScore: score };
  });
  // Sort by score descending
  scored.sort((a, b) => b._rankScore - a._rankScore);
  // Return top 5
  const top = scored.slice(0, 5);
  console.log(`[Ranking] ${candidates.length} candidates → top ${top.length}: ${top.map(c => `${c.name} (score: ${Math.round(c._rankScore)})`).join(", ")}`);
  return top;
}

async function fetchRaceData(apiKey, state, district, office) {
  // Load legislator photo data and Polymarket odds in parallel with candidate search
  const [_, fecCands2026, polymarketOdds] = await Promise.all([
    loadLegislatorData(),
    fetchFECCandidates(apiKey, state, district, office, 2026),
    fetchPolymarketOdds(state, office),
  ]);

  let fecCands = fecCands2026;
  let usedYear = 2026;

  if (!fecCands.length) {
    fecCands = await fetchFECCandidates(apiKey, state, district, office, 2024);
    usedYear = 2024;
  }

  if (!fecCands.length) return { candidates: [], source: "fec" };

  const fins = await Promise.all(fecCands.map(c => fetchCandidateTotals(apiKey, c.candidate_id, usedYear).catch(() => null)));
  const candidates = fecCands.map((c, i) => {
    const cand = transformFECCandidate(c, fins[i], `${state}-${district || "SEN"}`, i);
    // Look up hardcoded bridge score
    const bs = lookupBridgeScore(cand.name);
    if (bs) {
      cand.bridgeScore = bs.score;
      cand.bridgeExplanation = bs.explanation;
    }
    // Attach photo URL if we can find the bioguide ID
    const bioguide = getBioguideId(cand, state);
    if (bioguide) {
      cand.photoUrl = getPhotoUrl(bioguide);
      cand.bioguideId = bioguide;
      console.log(`[Photos] ✓ ${cand.name} → ${bioguide} → ${cand.photoUrl}`);
    } else {
      console.log(`[Photos] ✕ No congressional photo for ${cand.name} — will try Wikipedia`);
    }
    return cand;
  });

  // Wikipedia fallback: fetch photos for candidates without congressional photos
  const wikiPromises = candidates.map(async (cand) => {
    if (!cand.photoUrl) {
      const wikiUrl = await fetchWikipediaPhoto(cand.name, state);
      if (wikiUrl) {
        cand.photoUrl = wikiUrl;
        cand.photoSource = "wikipedia";
      }
    }
  });
  await Promise.all(wikiPromises);

  // Rank candidates and return top 5 most noteworthy
  const topCandidates = rankAndFilterCandidates(candidates, polymarketOdds);

  return { candidates: topCandidates, source: "fec", year: usedYear, totalCandidates: candidates.length };
}

// ═══════════════════════════════════════════════════════════════════════════
// CANDIDATE PHOTO SERVICE
// ═══════════════════════════════════════════════════════════════════════════
// Uses the unitedstates/images GitHub repo for official congressional photos.
// Photos are keyed by Bioguide ID. We map FEC candidate ID → Bioguide ID
// via the @unitedstates/congress-legislators YAML (loaded as JSON from GitHub).
// For candidates without photos, we fall back to the initial-based avatar.
// ═══════════════════════════════════════════════════════════════════════════

const PHOTO_BASE = "https://raw.githubusercontent.com/unitedstates/images/gh-pages/congress";
const legislatorCache = { loaded: false, byFecId: {}, byName: {}, byLastState: {}, allLegislators: [] };

async function loadLegislatorData() {
  if (legislatorCache.loaded) return;
  try {
    const res = await fetch("https://unitedstates.github.io/congress-legislators/legislators-current.json");
    if (!res.ok) throw new Error("Failed to fetch legislator data");
    const legislators = await res.json();

    console.log(`[Photos] Loaded ${legislators.length} current legislators`);
    legislatorCache.allLegislators = legislators;

    for (const leg of legislators) {
      const bioguide = leg.id?.bioguide;
      if (!bioguide) continue;

      // Index by ALL known FEC IDs
      const fecIds = leg.id?.fec || [];
      for (const fid of fecIds) {
        legislatorCache.byFecId[fid] = bioguide;
      }

      // Index by multiple name variants
      const last = (leg.name?.last || "").toLowerCase().trim();
      const first = (leg.name?.first || "").toLowerCase().trim();
      const nickname = (leg.name?.nickname || "").toLowerCase().trim();
      const official = (leg.name?.official_full || "").toLowerCase().trim();

      if (last && first) {
        legislatorCache.byName[`${first}|${last}`] = bioguide;
        if (nickname) legislatorCache.byName[`${nickname}|${last}`] = bioguide;
      }
      // Also index by official_full for exact full-name matching
      if (official) legislatorCache.byName[`full:${official}`] = bioguide;

      // Index by last name + state (most recent term)
      const state = leg.terms?.[leg.terms.length - 1]?.state;
      if (last && state) {
        const key = `${last}|${state.toLowerCase()}`;
        // Only store if not already set (avoid collisions for common last names)
        if (!legislatorCache.byLastState[key]) {
          legislatorCache.byLastState[key] = bioguide;
        } else {
          // Mark as ambiguous so we don't use it (e.g. two "Smith" in same state)
          legislatorCache.byLastState[key] = "AMBIGUOUS";
        }
      }
    }
    legislatorCache.loaded = true;
    console.log(`[Photos] Indexed ${Object.keys(legislatorCache.byFecId).length} FEC IDs, ${Object.keys(legislatorCache.byName).length} name variants`);
  } catch (err) {
    console.warn("[Photos] Could not load legislator data:", err.message);
    legislatorCache.loaded = true;
  }
}

function normalizeForMatch(str) {
  // Remove suffixes, periods, hyphens for fuzzy matching
  return (str || "").toLowerCase().trim()
    .replace(/\b(jr|sr|ii|iii|iv|md|phd|esq)\.?\b/gi, "")
    .replace(/[.\-']/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

function getBioguideId(candidate, stateAbbr) {
  // Strategy 1: Direct FEC ID match
  if (candidate.fecId && legislatorCache.byFecId[candidate.fecId]) {
    console.log(`[Photos] Matched ${candidate.name} via FEC ID ${candidate.fecId}`);
    return legislatorCache.byFecId[candidate.fecId];
  }

  const first = normalizeForMatch(candidate.firstName);
  const last = normalizeForMatch(candidate.lastName);
  const fullName = normalizeForMatch(candidate.name);

  // Strategy 2: Exact first|last match
  if (first && last) {
    const exact = legislatorCache.byName[`${first}|${last}`];
    if (exact) { console.log(`[Photos] Matched ${candidate.name} via exact name`); return exact; }
  }

  // Strategy 3: Full name match
  if (fullName) {
    const fullMatch = legislatorCache.byName[`full:${fullName}`];
    if (fullMatch) { console.log(`[Photos] Matched ${candidate.name} via full name`); return fullMatch; }
  }

  // Strategy 4: Last name + state (skip if ambiguous)
  if (last && stateAbbr) {
    const stateMatch = legislatorCache.byLastState[`${last}|${stateAbbr.toLowerCase()}`];
    if (stateMatch && stateMatch !== "AMBIGUOUS") {
      console.log(`[Photos] Matched ${candidate.name} via last name + state`);
      return stateMatch;
    }
  }

  // Strategy 5: Scan all legislators for partial/fuzzy match
  // This catches cases like "Ted" vs "Edward", hyphenated names, etc.
  for (const leg of legislatorCache.allLegislators) {
    const bio = leg.id?.bioguide;
    if (!bio) continue;
    const legLast = normalizeForMatch(leg.name?.last);
    const legFirst = normalizeForMatch(leg.name?.first);
    const legNick = normalizeForMatch(leg.name?.nickname);
    const legState = leg.terms?.[leg.terms.length - 1]?.state;

    // Must match state if we know it
    if (stateAbbr && legState && legState !== stateAbbr) continue;

    // Check if last names match (required)
    if (!legLast || legLast !== last) continue;

    // Last name matches + same state — check first name loosely
    if (legFirst && first) {
      // First name starts with same letters (Ted/Theodore, Bob/Robert won't match, but Bill/William won't either — that's OK)
      if (legFirst === first || legFirst.startsWith(first) || first.startsWith(legFirst)) {
        console.log(`[Photos] Matched ${candidate.name} via fuzzy scan (${legFirst} ${legLast})`);
        return bio;
      }
      // Check nickname
      if (legNick && (legNick === first || legNick.startsWith(first) || first.startsWith(legNick))) {
        console.log(`[Photos] Matched ${candidate.name} via nickname scan (${legNick} ${legLast})`);
        return bio;
      }
    }
  }

  return null;
}

function getPhotoUrl(bioguideId, size = "225x275") {
  // Sizes: original, 225x275, 450x550
  return `${PHOTO_BASE}/${size}/${bioguideId}.jpg`;
}

// ═══════════════════════════════════════════════════════════════════════════
// WIKIPEDIA PHOTO FALLBACK
// ═══════════════════════════════════════════════════════════════════════════
// For candidates without congressional photos (challengers, first-time
// candidates), we try Wikipedia's API to find a photo from their page.
// Uses the MediaWiki API with page images (og:image) endpoint.
// ═══════════════════════════════════════════════════════════════════════════

const wikiPhotoCache = {};

async function fetchWikipediaPhoto(candidateName, stateAbbr) {
  const cacheKey = `${candidateName}|${stateAbbr}`;
  if (wikiPhotoCache[cacheKey] !== undefined) return wikiPhotoCache[cacheKey];

  try {
    // Clean the candidate name — remove suffixes that confuse Wikipedia search
    const cleanName = candidateName
      .replace(/\b(Jr\.?|Sr\.?|II|III|IV|MD|PhD|Esq\.?)\b/gi, "")
      .replace(/\s+/g, " ").trim();

    // Try multiple search strategies
    const searches = [
      `${cleanName} politician ${stateAbbr}`,
      `${cleanName} ${stateAbbr}`,
      cleanName,
    ];

    const nameParts = cleanName.toLowerCase().split(/\s+/);
    const lastName = nameParts[nameParts.length - 1];
    const firstName = nameParts[0];

    for (const searchQuery of searches) {
      const searchUrl = `https://en.wikipedia.org/w/api.php?action=query&list=search&srsearch=${encodeURIComponent(searchQuery)}&srlimit=5&format=json&origin=*`;
      const searchRes = await fetch(searchUrl);
      if (!searchRes.ok) continue;
      const searchData = await searchRes.json();
      const results = searchData.query?.search || [];

      for (const r of results) {
        const title = r.title.toLowerCase();
        // Title must contain last name
        if (!title.includes(lastName)) continue;
        // Title should contain first name OR be a close match
        if (!title.includes(firstName) && nameParts.length > 1) continue;
        // Skip disambiguation pages and lists
        if (r.snippet && (r.snippet.includes("may refer to") || r.snippet.includes("disambiguation"))) continue;

        // Found a likely match — fetch its image
        const imgUrl = `https://en.wikipedia.org/w/api.php?action=query&titles=${encodeURIComponent(r.title)}&prop=pageimages&pithumbsize=300&format=json&origin=*`;
        const imgRes = await fetch(imgUrl);
        if (!imgRes.ok) continue;
        const imgData = await imgRes.json();
        const pages = imgData.query?.pages || {};
        const page = Object.values(pages)[0];
        const thumbnail = page?.thumbnail?.source || null;

        if (thumbnail) {
          console.log(`[WikiPhoto] ✓ ${candidateName} → "${r.title}" → ${thumbnail}`);
          wikiPhotoCache[cacheKey] = thumbnail;
          return thumbnail;
        }
      }
    }

    console.log(`[WikiPhoto] ✕ No Wikipedia photo found for "${candidateName}"`);
    wikiPhotoCache[cacheKey] = null;
    return null;
  } catch (err) {
    console.warn(`[WikiPhoto] Error for ${candidateName}:`, err.message);
    wikiPhotoCache[cacheKey] = null;
    return null;
  }
}

// ═══════════════════════════════════════════════════════════════════════════
// CENSUS GEOCODER — accurate zip → congressional district
// ═══════════════════════════════════════════════════════════════════════════
// Uses the US Census Bureau's geocoder API to resolve a zip code to a
// precise congressional district. Falls back to the simple prefix table
// if the geocoder is unavailable.
//
// Note: The Census geocoder works best with full addresses, but zip codes
// alone give reasonable results by geocoding the zip centroid.
// ═══════════════════════════════════════════════════════════════════════════

async function geocodeZipToDistrict(zip) {
  try {
    // Use Census geocoder — geocode the zip code as a one-line address
    const url = `https://geocoding.geo.census.gov/geocoder/geographies/onelineaddress?address=${zip}&benchmark=Public_AR_Current&vintage=Current_Current&format=json`;
    const res = await fetch(url);
    if (!res.ok) throw new Error("Census API error");
    const data = await res.json();

    const matches = data?.result?.addressMatches;
    if (matches && matches.length > 0) {
      const geographies = matches[0].geographies;
      // Look for congressional district in the geography layers
      const cdLayer = geographies?.["Congressional Districts"] ||
                      geographies?.["118th Congressional Districts"] ||
                      geographies?.["119th Congressional Districts"];
      if (cdLayer && cdLayer.length > 0) {
        const cd = cdLayer[0];
        const stateAbbr = cd.STUSAB || stateFromFips(cd.STATE);
        const districtNum = parseInt(cd.CD || cd.CDFP || cd.CD119 || cd.CD118 || "0", 10);
        if (stateAbbr) {
          return { stateAbbr, district: districtNum || 1, source: "census" };
        }
      }
      // Fallback: use state from the match even without CD
      const stFips = matches[0].geographies?.["States"]?.[0]?.STUSAB;
      if (stFips) {
        return { stateAbbr: stFips, district: 1, source: "census-state-only" };
      }
    }
    return null;
  } catch (err) {
    console.warn("Census geocoder failed, using fallback:", err);
    return null;
  }
}

const FIPS_TO_STATE = {
  "01":"AL","02":"AK","04":"AZ","05":"AR","06":"CA","08":"CO","09":"CT","10":"DE",
  "11":"DC","12":"FL","13":"GA","15":"HI","16":"ID","17":"IL","18":"IN","19":"IA",
  "20":"KS","21":"KY","22":"LA","23":"ME","24":"MD","25":"MA","26":"MI","27":"MN",
  "28":"MS","29":"MO","30":"MT","31":"NE","32":"NV","33":"NH","34":"NJ","35":"NM",
  "36":"NY","37":"NC","38":"ND","39":"OH","40":"OK","41":"OR","42":"PA","44":"RI",
  "45":"SC","46":"SD","47":"TN","48":"TX","49":"UT","50":"VT","51":"VA","53":"WA",
  "54":"WV","55":"WI","56":"WY",
};
function stateFromFips(fips) { return FIPS_TO_STATE[fips] || null; }

// ═══════════════════════════════════════════════════════════════════════════
// MOCK DATA FALLBACK
// ═══════════════════════════════════════════════════════════════════════════
const FIRST_NAMES = ["James","Maria","David","Sarah","Michael","Linda","Robert","Patricia","William","Jennifer","Thomas","Elizabeth","Richard","Barbara","Charles","Susan","Joseph","Margaret","Daniel","Dorothy","Christopher","Lisa","Matthew","Nancy","Anthony","Karen","Andrew","Betty","Joshua","Helen","Kenneth","Sandra","Steven","Emily","George","Ashley","Edward","Donna","Brian","Carol"];
const LAST_NAMES = ["Anderson","Mitchell","Rivera","Thompson","Nakamura","Okafor","Sullivan","Blackwood","Chen","Delgado","Foster","Gupta","Harrison","Ibrahim","Johansson","Kowalski","Lindberg","Morales","Novak","O'Brien","Petrov","Quinn","Ramirez","Steinberg","Takahashi","Underwood","Vasquez","Whitfield","Xiong","Yamamoto"];
const MOCK_SRC = [{ name: "Small Donors (<$200)", color: "#4ECDC4" },{ name: "Large Donors ($200+)", color: "#45B7D1" },{ name: "PACs", color: "#F7DC6F" },{ name: "Self-Funded", color: "#E8A87C" },{ name: "Business & Industry", color: "#C38D9E" },{ name: "Labor Organizations", color: "#85CDCA" }];

function rng(seed) { let s = seed; return () => { s = (s * 16807) % 2147483647; return (s - 1) / 2147483646; }; }
function pick(arr, r) { return arr[Math.floor(r() * arr.length)]; }

function generateMockCandidates(districtId) {
  const seed = districtId.split('').reduce((a, c) => a + c.charCodeAt(0), 0) * 137;
  const r = rng(seed); const n = Math.floor(r() * 3) + 2; const cands = []; const used = new Set();
  for (let i = 0; i < n; i++) {
    let first, last, full;
    do { first = pick(FIRST_NAMES, r); last = pick(LAST_NAMES, r); full = `${first} ${last}`; } while (used.has(full)); used.add(full);
    const total = Math.floor(r() * 8e6) + 5e5; const srcs = []; let rem = 100;
    const picked = [...MOCK_SRC].sort(() => r() - 0.5).slice(0, 3 + Math.floor(r() * 3));
    picked.forEach((s, j) => { const p = j === picked.length - 1 ? rem : Math.floor(r() * rem * 0.6) + 5; rem -= p; srcs.push({ ...s, percentage: p }); });
    const isInc = i === 0 && r() > 0.4;
    cands.push({ id: `${districtId}-${i}`, name: full, firstName: first, lastName: last, avatarSeed: Math.floor(r() * 10000),
      bridgeScore: Math.floor(r() * 5) + 1, totalFunding: total, fundingSources: srcs,
      isIncumbent: isInc, yearsHeld: isInc ? Math.floor(r() * 20) + 2 : 0,
      dataSource: "mock" });
  }
  return cands;
}

// ═══════════════════════════════════════════════════════════════════════════
// GEOGRAPHY
// ═══════════════════════════════════════════════════════════════════════════
const US_STATES = [
  {abbr:"AL",name:"Alabama",districts:7},{abbr:"AK",name:"Alaska",districts:1},{abbr:"AZ",name:"Arizona",districts:9},{abbr:"AR",name:"Arkansas",districts:4},
  {abbr:"CA",name:"California",districts:52},{abbr:"CO",name:"Colorado",districts:8},{abbr:"CT",name:"Connecticut",districts:5},{abbr:"DE",name:"Delaware",districts:1},
  {abbr:"FL",name:"Florida",districts:28},{abbr:"GA",name:"Georgia",districts:14},{abbr:"HI",name:"Hawaii",districts:2},{abbr:"ID",name:"Idaho",districts:2},
  {abbr:"IL",name:"Illinois",districts:17},{abbr:"IN",name:"Indiana",districts:9},{abbr:"IA",name:"Iowa",districts:4},{abbr:"KS",name:"Kansas",districts:4},
  {abbr:"KY",name:"Kentucky",districts:6},{abbr:"LA",name:"Louisiana",districts:6},{abbr:"ME",name:"Maine",districts:2},{abbr:"MD",name:"Maryland",districts:8},
  {abbr:"MA",name:"Massachusetts",districts:9},{abbr:"MI",name:"Michigan",districts:13},{abbr:"MN",name:"Minnesota",districts:8},{abbr:"MS",name:"Mississippi",districts:4},
  {abbr:"MO",name:"Missouri",districts:8},{abbr:"MT",name:"Montana",districts:2},{abbr:"NE",name:"Nebraska",districts:3},{abbr:"NV",name:"Nevada",districts:4},
  {abbr:"NH",name:"New Hampshire",districts:2},{abbr:"NJ",name:"New Jersey",districts:12},{abbr:"NM",name:"New Mexico",districts:3},{abbr:"NY",name:"New York",districts:26},
  {abbr:"NC",name:"North Carolina",districts:14},{abbr:"ND",name:"North Dakota",districts:1},{abbr:"OH",name:"Ohio",districts:15},{abbr:"OK",name:"Oklahoma",districts:5},
  {abbr:"OR",name:"Oregon",districts:6},{abbr:"PA",name:"Pennsylvania",districts:17},{abbr:"RI",name:"Rhode Island",districts:2},{abbr:"SC",name:"South Carolina",districts:7},
  {abbr:"SD",name:"South Dakota",districts:1},{abbr:"TN",name:"Tennessee",districts:9},{abbr:"TX",name:"Texas",districts:38},{abbr:"UT",name:"Utah",districts:4},
  {abbr:"VT",name:"Vermont",districts:1},{abbr:"VA",name:"Virginia",districts:11},{abbr:"WA",name:"Washington",districts:10},{abbr:"WV",name:"West Virginia",districts:2},
  {abbr:"WI",name:"Wisconsin",districts:8},{abbr:"WY",name:"Wyoming",districts:1},
];
// States with a 2026 U.S. Senate race (Class II regular elections + FL/OH special elections)
const SENATE_RACES_2026 = new Set([
  "AL","AK","AR","CO","DE","FL","GA","ID","IL","IA","KS","KY",
  "LA","ME","MA","MI","MN","MS","MT","NE","NH","NJ","NM","NC",
  "OH","OK","OR","RI","SC","SD","TN","TX","VA","WV","WY"
]);

const ZIP_RANGES = [
  ["010","027","MA"],["028","029","RI"],["030","038","NH"],["039","049","ME"],["050","059","VT"],["060","069","CT"],["070","089","NJ"],
  ["100","149","NY"],["150","196","PA"],["197","199","DE"],["200","205","DC"],["206","219","MD"],["220","246","VA"],["247","268","WV"],
  ["270","289","NC"],["290","299","SC"],["300","319","GA"],["320","349","FL"],["350","369","AL"],["370","385","TN"],["386","397","MS"],
  ["400","427","KY"],["430","458","OH"],["460","479","IN"],["480","499","MI"],["500","528","IA"],["530","549","WI"],["550","567","MN"],
  ["570","577","SD"],["580","588","ND"],["590","599","MT"],["600","629","IL"],["630","658","MO"],["660","679","KS"],["680","693","NE"],
  ["700","714","LA"],["716","729","AR"],["730","749","OK"],["750","799","TX"],["800","816","CO"],["820","831","WY"],["832","838","ID"],
  ["840","847","UT"],["850","865","AZ"],["870","884","NM"],["889","898","NV"],["900","961","CA"],["967","968","HI"],["970","979","OR"],
  ["980","994","WA"],["995","999","AK"],
];
function zipToDistrict(zip) {
  const p = zip.substring(0, 3);
  const match = ZIP_RANGES.find(([min, max]) => p >= min && p <= max);
  if (!match) return null;
  const state = US_STATES.find(s => s.abbr === match[2]);
  if (!state) return null;
  return { state, district: (parseInt(zip) % state.districts) + 1 };
}

// ═══════════════════════════════════════════════════════════════════════════
// UI COMPONENTS
// ═══════════════════════════════════════════════════════════════════════════
const BL = ["","Very Polarizing","Somewhat Polarizing","Neutral","Somewhat Bridge-Builder","Strong Bridge-Builder"];
const BC = ["","#E74C3C","#E67E22","#95A5A6","#3498DB","#27AE60"];
const BB = ["","#FDEDEC","#FDF2E9","#F2F3F4","#EBF5FB","#E8F8F5"];
const F = "'DM Sans', sans-serif";
const PF = "'Playfair Display', serif";

function BridgeScale({ score, explanation }) {
  const [showExplanation, setShowExplanation] = useState(false);
  return (
    <div style={{ margin: "10px 0" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
        <span style={{ fontSize: 11, color: "#8B8FA3", fontFamily: F, letterSpacing: 1, textTransform: "uppercase" }}>Bridge Score</span>
        {score !== null ? (
          <>
            <span style={{ fontSize: 11, fontWeight: 700, color: BC[score], background: BB[score], padding: "2px 8px", borderRadius: 20, fontFamily: F }}>{BL[score]}</span>
            {explanation && (
              <span
                title="Find out what's behind the bridge score"
                onClick={(e) => { e.stopPropagation(); setShowExplanation(!showExplanation); }}
                style={{ width: 18, height: 18, borderRadius: "50%", border: `1.5px solid ${BC[score] || "#8B8FA3"}`,
                  display: "inline-flex", alignItems: "center", justifyContent: "center",
                  fontSize: 11, fontWeight: 700, color: BC[score] || "#8B8FA3", cursor: "pointer",
                  transition: "all 0.2s", background: showExplanation ? (BB[score] || "#F3F4F6") : "transparent",
                  fontFamily: F, lineHeight: 1, flexShrink: 0 }}
              >?</span>
            )}
          </>
        ) : (
          <span style={{ fontSize: 11, color: "#9CA3AF", fontStyle: "italic", fontFamily: F }}>Not yet available</span>
        )}
      </div>
      <div style={{ display: "flex", gap: 3 }}>
        {[1,2,3,4,5].map(i => (<div key={i} style={{ width: 36, height: 8, borderRadius: 4, background: score !== null && i <= score ? BC[score] : "#E8E9ED", transition: "all 0.3s" }} />))}
      </div>
      {showExplanation && explanation && (
        <div style={{ marginTop: 10, padding: "14px 16px", background: BB[score] || "#F8F9FB", borderRadius: 12,
          border: `1px solid ${BC[score] || "#E8E9ED"}22`, position: "relative", animation: "fadeIn 0.3s ease" }}>
          <button onClick={(e) => { e.stopPropagation(); setShowExplanation(false); }}
            style={{ position: "absolute", top: 8, right: 10, background: "none", border: "none", cursor: "pointer",
              fontSize: 16, color: "#9CA3AF", fontFamily: F, lineHeight: 1 }}>×</button>
          <div style={{ fontSize: 11, fontWeight: 700, color: BC[score] || "#374151", textTransform: "uppercase",
            letterSpacing: 1, marginBottom: 6, fontFamily: F }}>Why this score?</div>
          <p style={{ fontSize: 13, color: "#374151", lineHeight: 1.6, margin: 0, fontFamily: F, paddingRight: 16 }}>{explanation}</p>
        </div>
      )}
    </div>
  );
}

function FundingBar({ sources, total, cashOnHand, totalSpent, debt, coverageDates }) {
  const fmt = v => v >= 1e6 ? `$${(v/1e6).toFixed(1)}M` : v >= 1e3 ? `$${(v/1e3).toFixed(0)}K` : `$${v.toLocaleString()}`;
  if (!total) return <div style={{ margin: "10px 0", padding: "12px 16px", background: "#F8F9FB", borderRadius: 10 }}><span style={{ fontSize: 12, color: "#9CA3AF", fontStyle: "italic", fontFamily: F }}>No funding data reported yet</span></div>;
  return (
    <div style={{ margin: "10px 0" }}>
      {/* Financial Overview Cards */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8, marginBottom: 12 }}>
        <div style={{ background: "#F0FDF4", borderRadius: 10, padding: "10px 12px", textAlign: "center" }}>
          <div style={{ fontSize: 10, color: "#6B7280", fontFamily: F, letterSpacing: 0.5, textTransform: "uppercase", marginBottom: 2 }}>Raised</div>
          <div style={{ fontSize: 16, fontWeight: 700, color: "#16A34A", fontFamily: F }}>{fmt(total)}</div>
        </div>
        <div style={{ background: "#FEF3C7", borderRadius: 10, padding: "10px 12px", textAlign: "center" }}>
          <div style={{ fontSize: 10, color: "#6B7280", fontFamily: F, letterSpacing: 0.5, textTransform: "uppercase", marginBottom: 2 }}>Spent</div>
          <div style={{ fontSize: 16, fontWeight: 700, color: "#D97706", fontFamily: F }}>{fmt(totalSpent || 0)}</div>
        </div>
        <div style={{ background: "#EFF6FF", borderRadius: 10, padding: "10px 12px", textAlign: "center" }}>
          <div style={{ fontSize: 10, color: "#6B7280", fontFamily: F, letterSpacing: 0.5, textTransform: "uppercase", marginBottom: 2 }}>Cash on Hand</div>
          <div style={{ fontSize: 16, fontWeight: 700, color: "#2563EB", fontFamily: F }}>{fmt(cashOnHand || 0)}</div>
        </div>
      </div>
      {debt > 0 && (
        <div style={{ background: "#FEF2F2", borderRadius: 8, padding: "6px 12px", marginBottom: 10, display: "flex", alignItems: "center", gap: 6 }}>
          <span style={{ fontSize: 11, color: "#DC2626", fontWeight: 600, fontFamily: F }}>⚠ Debt: {fmt(debt)}</span>
        </div>
      )}
      {/* Funding Sources Bar */}
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
        <span style={{ fontSize: 11, color: "#8B8FA3", fontFamily: F, letterSpacing: 1, textTransform: "uppercase" }}>Funding Sources</span>
        {coverageDates && (
          <span style={{ fontSize: 10, color: "#9CA3AF", fontFamily: F }}>
            {new Date(coverageDates.start).toLocaleDateString("en-US", {month:"short",year:"numeric"})} – {new Date(coverageDates.end).toLocaleDateString("en-US", {month:"short",year:"numeric"})}
          </span>
        )}
      </div>
      <div style={{ display: "flex", borderRadius: 6, overflow: "hidden", height: 10, background: "#E8E9ED" }}>
        {sources.map((s, i) => <div key={i} title={`${s.name}: ${s.percentage}%`} style={{ width: `${s.percentage}%`, background: s.color, transition: "width 0.5s" }} />)}
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: "4px 12px", marginTop: 6 }}>
        {sources.map((s, i) => (
          <div key={i} style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <div style={{ width: 8, height: 8, borderRadius: 2, background: s.color }} />
            <span style={{ fontSize: 10, color: "#6B7280", fontFamily: F }}>{s.name} ({s.percentage}%{s.amount ? ` · ${fmt(s.amount)}` : ""})</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function Avatar({ name, seed, size = 64, photoUrl = null }) {
  const [imgError, setImgError] = useState(false);
  const colors = ["#4ECDC4","#45B7D1","#96CEB4","#FFEAA7","#DDA0DD","#F7DC6F","#85CDCA","#E8A87C","#C38D9E","#D4A5A5"];

  if (photoUrl && !imgError) {
    return (
      <div style={{ width: size, height: size, borderRadius: "50%", overflow: "hidden",
        boxShadow: "0 4px 14px rgba(0,0,0,0.12)", flexShrink: 0, background: "#E8E9ED" }}>
        <img src={photoUrl} alt={name}
          onError={() => setImgError(true)}
          style={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: "center top" }} />
      </div>
    );
  }

  return (
    <div style={{ width: size, height: size, borderRadius: "50%", background: `linear-gradient(135deg, ${colors[seed % 10]}, ${colors[seed % 10]}dd)`,
      display: "flex", alignItems: "center", justifyContent: "center", fontSize: size * 0.35, fontWeight: 700, color: "#fff", fontFamily: PF,
      boxShadow: `0 4px 14px ${colors[seed % 10]}44`, flexShrink: 0 }}>
      {name.split(' ').map(n => n[0]).join('').substring(0, 2)}
    </div>
  );
}

function DataBadge({ source }) {
  const live = source === "fec";
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 10, fontWeight: 600, padding: "3px 8px", borderRadius: 20,
      background: live ? "#E8F8F5" : "#FFF8E1", color: live ? "#27AE60" : "#F59E0B", fontFamily: F }}>
      <span style={{ width: 6, height: 6, borderRadius: "50%", background: live ? "#27AE60" : "#F59E0B" }} />
      {live ? "FEC DATA" : "SIMULATED"}
    </span>
  );
}

function CandidateCard({ candidate: c, index }) {
  return (
    <div style={{ background: "#FFF", borderRadius: 16, boxShadow: "0 1px 3px rgba(0,0,0,0.06), 0 8px 24px rgba(0,0,0,0.04)",
      border: c.isIncumbent ? "2px solid #4ECDC4" : "1px solid #F0F1F3", overflow: "hidden", animation: `fadeIn 0.4s ease ${index*0.1}s both` }}>
      {c.isIncumbent && (
        <div style={{ background: "linear-gradient(90deg, #4ECDC4, #45B7D1)", padding: "6px 16px" }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: "#fff", fontFamily: F, letterSpacing: 1 }}>★ INCUMBENT{c.yearsHeld > 0 ? ` · ${c.yearsHeld} YEARS` : ""}</span>
        </div>
      )}
      <div style={{ padding: "20px 24px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 16 }}>
          <Avatar name={c.name} seed={c.avatarSeed} size={72} photoUrl={c.photoUrl} />
          <div style={{ flex: 1 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 4 }}>
              <h3 style={{ fontSize: 20, fontWeight: 700, color: "#1A1D2E", margin: 0, fontFamily: PF }}>{c.name}</h3>
              <DataBadge source={c.dataSource} />
            </div>
            {c.fecId && <div style={{ fontSize: 10, color: "#9CA3AF", fontFamily: F, marginBottom: 4 }}>FEC ID: {c.fecId}</div>}
            <BridgeScale score={c.bridgeScore} explanation={c.bridgeExplanation} />
          </div>
        </div>
        <FundingBar sources={c.fundingSources} total={c.totalFunding} cashOnHand={c.cashOnHand} totalSpent={c.totalSpent} debt={c.debt} coverageDates={c.coverageDates} />
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// LEAFLET US STATES MAP (HOME PAGE)
// ═══════════════════════════════════════════════════════════════════════════

const statesGeoCache = { data: null };

async function fetchStatesGeo() {
  if (statesGeoCache.data) return statesGeoCache.data;
  try {
    // PublicaMundi US States GeoJSON — lightweight, CORS-friendly from GitHub
    const url = "https://raw.githubusercontent.com/PublicaMundi/MappingAPI/master/data/geojson/us-states.json";
    const res = await fetch(url);
    if (!res.ok) throw new Error(`GitHub: ${res.status}`);
    const data = await res.json();
    // Enrich features with state abbreviation using existing FIPS_TO_STATE lookup
    for (const f of data.features) {
      const fips = String(f.id).padStart(2, "0");
      f.properties.STUSAB = FIPS_TO_STATE[fips] || "";
      f.properties.NAME = f.properties.name || "";
    }
    statesGeoCache.data = data;
    console.log(`[Map] Loaded ${data.features.length} state boundaries`);
    return data;
  } catch (err) {
    console.warn("[Map] Failed to load state boundaries:", err);
    return null;
  }
}

function USLeafletMap({ onStateClick, selectedState }) {
  const [geoData, setGeoData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hovered, setHovered] = useState(null);
  const svgRef = useRef(null);

  useEffect(() => {
    fetchStatesGeo().then(data => {
      setGeoData(data);
      setLoading(false);
    });
  }, []);

  // Simple Albers-like projection for continental US
  const project = useCallback((lon, lat) => {
    const x = (lon + 96) * 8.5 + 420;
    const y = (50 - lat) * 12 + 30;
    return [x, y];
  }, []);

  // Project for Alaska (scaled down, shifted to bottom-left)
  const projectAK = useCallback((lon, lat) => {
    // Handle wrapping for Aleutian Islands (lon > 0 means it wrapped past 180)
    let adjustedLon = lon;
    if (lon > 0) adjustedLon = lon - 360;
    const x = (adjustedLon + 170) * 2.2 + 80;
    const y = (72 - lat) * 2.8 + 410;
    return [x, y];
  }, []);

  // Project for Hawaii (scaled up, positioned bottom-left near Alaska)
  const projectHI = useCallback((lon, lat) => {
    const x = (lon + 160) * 12 + 250;
    const y = (22 - lat) * 12 + 460;
    return [x, y];
  }, []);

  const featureToPath = useCallback((feature) => {
    const abbr = feature.properties.STUSAB;
    const proj = abbr === "AK" ? projectAK : abbr === "HI" ? projectHI : project;

    const processCoords = (coords) => {
      return coords.map((ring, ri) => {
        const points = ring.map(([lon, lat]) => proj(lon, lat));
        // Skip tiny rings
        if (points.length < 3) return "";
        return points.map((p, i) => `${i === 0 ? "M" : "L"}${p[0].toFixed(1)},${p[1].toFixed(1)}`).join("") + "Z";
      }).join("");
    };

    const geom = feature.geometry;
    if (geom.type === "Polygon") {
      return processCoords(geom.coordinates);
    } else if (geom.type === "MultiPolygon") {
      return geom.coordinates.map(poly => processCoords(poly)).join("");
    }
    return "";
  }, [project, projectAK, projectHI]);

  const features = useMemo(() => {
    if (!geoData || !geoData.features) return [];
    return geoData.features
      .filter(f => f.properties.STUSAB) // Only US states
      .map(f => ({
        abbr: f.properties.STUSAB,
        name: f.properties.NAME,
        path: featureToPath(f),
        feature: f,
      }))
      .filter(f => f.path); // Skip empty paths
  }, [geoData, featureToPath]);

  const continental = features.filter(f => f.abbr !== "AK" && f.abbr !== "HI");
  const alaska = features.find(f => f.abbr === "AK");
  const hawaii = features.find(f => f.abbr === "HI");

  const getStyle = (abbr) => {
    const isSel = selectedState === abbr;
    const isHov = hovered === abbr;
    const hasSenate = SENATE_RACES_2026.has(abbr);
    return {
      fill: isSel ? "#4ECDC4" : isHov ? (hasSenate ? "#7DD8B0" : "#A8E6CF") : hasSenate ? "#A3D5C9" : "#D1E8E4",
      fillOpacity: isSel ? 0.8 : isHov ? 0.75 : hasSenate ? 0.7 : 0.55,
      stroke: isSel ? "#2DA89E" : isHov ? "#4ECDC4" : hasSenate ? "#6BAF9E" : "#8B9DAF",
      strokeWidth: isSel ? 2.5 : isHov ? 2 : 1,
      cursor: "pointer",
      transition: "all 0.15s ease",
    };
  };

  const handleClick = (abbr) => {
    const stateData = US_STATES.find(s => s.abbr === abbr);
    if (stateData && onStateClick) onStateClick(stateData);
  };

  return (
    <div style={{ borderRadius: 20, overflow: "hidden", border: "1px solid #E8ECF1", boxShadow: "0 1px 3px rgba(0,0,0,0.04), 0 8px 32px rgba(0,0,0,0.03)", background: "#FAFBFC", position: "relative" }}>
      {loading && (
        <div style={{ position: "absolute", zIndex: 500, top: 12, right: 12, display: "flex", alignItems: "center", gap: 6, background: "#fff", borderRadius: 8, padding: "6px 12px", boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }}>
          <div style={{ width: 12, height: 12, border: "2px solid #E8ECF1", borderTop: "2px solid #4ECDC4", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
          <span style={{ fontSize: 11, color: "#6B7280", fontFamily: F }}>Loading map...</span>
        </div>
      )}
      <svg ref={svgRef} viewBox="0 0 960 550" style={{ width: "100%", height: "auto", display: "block" }}>
        {/* Continental US */}
        {continental.map(f => (
          <path key={f.abbr} d={f.path} {...getStyle(f.abbr)}
            onMouseEnter={() => setHovered(f.abbr)}
            onMouseLeave={() => setHovered(null)}
            onClick={() => handleClick(f.abbr)}>
            <title>{f.name}</title>
          </path>
        ))}

        {/* Alaska inset box */}
        <rect x="60" y="395" width="200" height="120" rx="8" fill="#fff" fillOpacity="0.6" stroke="#CBD5E1" strokeWidth="1" />
        <text x="70" y="412" fontSize="10" fill="#8B9DAF" fontFamily={F} fontWeight="600">Alaska</text>
        {alaska && (
          <path d={alaska.path} {...getStyle("AK")}
            onMouseEnter={() => setHovered("AK")}
            onMouseLeave={() => setHovered(null)}
            onClick={() => handleClick("AK")}>
            <title>Alaska</title>
          </path>
        )}

        {/* Hawaii inset box */}
        <rect x="260" y="430" width="140" height="80" rx="8" fill="#fff" fillOpacity="0.6" stroke="#CBD5E1" strokeWidth="1" />
        <text x="270" y="447" fontSize="10" fill="#8B9DAF" fontFamily={F} fontWeight="600">Hawaii</text>
        {hawaii && (
          <path d={hawaii.path} {...getStyle("HI")}
            onMouseEnter={() => setHovered("HI")}
            onMouseLeave={() => setHovered(null)}
            onClick={() => handleClick("HI")}>
            <title>Hawaii</title>
          </path>
        )}

        {/* Legend */}
        <g transform="translate(680, 460)">
          <rect x="0" y="0" width="260" height="56" rx="8" fill="#fff" fillOpacity="0.85" stroke="#CBD5E1" strokeWidth="1" />
          <rect x="14" y="14" width="16" height="12" rx="3" fill="#A3D5C9" fillOpacity="0.7" stroke="#6BAF9E" strokeWidth="1" />
          <text x="38" y="24" fontSize="10" fill="#374151" fontFamily={F} fontWeight="500">States with 2026 US Senate Election</text>
          <rect x="14" y="34" width="16" height="12" rx="3" fill="#D1E8E4" fillOpacity="0.55" stroke="#8B9DAF" strokeWidth="1" />
          <text x="38" y="44" fontSize="10" fill="#374151" fontFamily={F} fontWeight="500">No 2026 Senate Election</text>
        </g>
      </svg>

      {/* Tooltip */}
      {hovered && (
        <div style={{ position: "absolute", top: 12, left: 12, background: "#fff", borderRadius: 8, padding: "6px 14px", boxShadow: "0 2px 8px rgba(0,0,0,0.12)", zIndex: 10 }}>
          <span style={{ fontSize: 14, fontWeight: 600, color: "#1A1D2E", fontFamily: F }}>{features.find(f => f.abbr === hovered)?.name || hovered}</span>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// LEAFLET DISTRICT MAP
// ═══════════════════════════════════════════════════════════════════════════
// Loads congressional district boundaries from Census TIGERweb ArcGIS API
// per state (on demand), rendered with Leaflet on OpenStreetMap tiles.
// ═══════════════════════════════════════════════════════════════════════════

// Reverse of FIPS_TO_STATE: abbreviation → FIPS code
const STATE_FIPS = {};
Object.entries(FIPS_TO_STATE).forEach(function([fips, abbr]) { STATE_FIPS[abbr] = fips; });

// State centers and zoom levels for initial map positioning
const STATE_CENTERS = {
  AL:[32.8,-86.8,7],AK:[64,-153,4],AZ:[34.3,-111.7,7],AR:[34.8,-92.4,7],CA:[37,-119.5,6],
  CO:[39,-105.5,7],CT:[41.6,-72.7,9],DE:[39,-75.5,9],FL:[28.5,-82.5,6],GA:[32.7,-83.5,7],
  HI:[20.5,-157,7],ID:[44.4,-114.6,6],IL:[40,-89.2,7],IN:[39.8,-86.3,7],IA:[42,-93.5,7],
  KS:[38.5,-98.3,7],KY:[37.8,-85.7,7],LA:[31,-92,7],ME:[45.4,-69,7],MD:[39.1,-76.8,8],
  MA:[42.2,-71.8,8],MI:[44.3,-84.5,7],MN:[46,-94.3,7],MS:[32.7,-89.7,7],MO:[38.4,-92.5,7],
  MT:[47,-110,6],NE:[41.5,-99.8,7],NV:[39,-117,6],NH:[43.7,-71.6,8],NJ:[40.2,-74.7,8],
  NM:[34.5,-106,7],NY:[43,-75.5,7],NC:[35.5,-80,7],ND:[47.5,-100.5,7],OH:[40.4,-82.8,7],
  OK:[35.5,-97.5,7],OR:[44,-120.5,7],PA:[41,-77.6,7],RI:[41.6,-71.5,10],SC:[33.9,-81,8],
  SD:[44.4,-100,7],TN:[35.9,-86.4,7],TX:[31.5,-99.5,6],UT:[39.5,-111.7,7],VT:[44,-72.7,8],
  VA:[37.5,-79,7],WA:[47.4,-120.5,7],WV:[38.6,-80.6,7],WI:[44.6,-90,7],WY:[43,-107.6,7],
};

const DISTRICT_COLORS = [
  "#4ECDC4","#45B7D1","#667EEA","#F7DC6F","#E8A87C","#C38D9E","#85CDCA","#96CEB4",
  "#DDA0DD","#FFEAA7","#74B9FF","#A29BFE","#FD79A8","#FDCB6E","#6C5CE7","#00CEC9",
  "#FF7675","#55EFC4","#B2BEC3","#636E72","#E17055","#00B894","#0984E3","#D63031",
  "#E84393","#2D3436","#6AB04C","#EB4D4B","#7ED6DF","#22A6B3","#F9CA24","#30336B",
  "#130F40","#535C68","#F19066","#786FA6","#CF6A87","#63CDDA","#EA8685","#596275",
  "#574B90","#303952","#E66767","#3DC1D3","#F5CD79","#546DE5","#E15F41","#C44569",
  "#3B3B98","#FC427B","#BDC581","#EAB543",
];

const districtGeoCache = {};

async function fetchDistrictGeo(stateAbbr) {
  if (districtGeoCache[stateAbbr]) return districtGeoCache[stateAbbr];
  const fips = STATE_FIPS[stateAbbr];
  if (!fips) return null;
  try {
    // Census TIGERweb ArcGIS API — query 119th Congressional Districts by state FIPS
    const url = `https://tigerweb.geo.census.gov/arcgis/rest/services/TIGERweb/Legislative/MapServer/0/query?where=STATE='${fips}'&outFields=STATE,BASENAME,CD119,GEOID&f=geojson&outSR=4326`;
    const res = await fetch(url);
    if (!res.ok) throw new Error(`Census API: ${res.status}`);
    const data = await res.json();
    districtGeoCache[stateAbbr] = data;
    return data;
  } catch (err) {
    console.warn("[Map] Failed to load districts for", stateAbbr, err);
    return null;
  }
}

function DistrictMap({ stateAbbr, stateName, districts, onDistrictClick, onSenateClick }) {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const [geoData, setGeoData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hoveredDistrict, setHoveredDistrict] = useState(null);

  // Load GeoJSON when state changes
  useEffect(() => {
    setLoading(true);
    setError(null);
    setGeoData(null);
    fetchDistrictGeo(stateAbbr).then(data => {
      if (data && data.features?.length > 0) {
        setGeoData(data);
      } else {
        setError("District boundaries not available for this state");
      }
      setLoading(false);
    });
  }, [stateAbbr]);

  // Initialize and update Leaflet map
  useEffect(() => {
    if (!mapRef.current) return;

    // Destroy existing map
    if (mapInstanceRef.current) {
      mapInstanceRef.current.remove();
      mapInstanceRef.current = null;
    }

    const center = STATE_CENTERS[stateAbbr] || [39.8, -98.5, 5];
    const map = L.map(mapRef.current, {
      center: [center[0], center[1]],
      zoom: center[2],
      zoomControl: true,
      scrollWheelZoom: true,
    });
    mapInstanceRef.current = map;

    // OpenStreetMap tiles with light style
    L.tileLayer("https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png", {
      attribution: '&copy; <a href="https://www.openstreetmap.org">OSM</a> &copy; <a href="https://carto.com">CARTO</a>',
      maxZoom: 18,
    }).addTo(map);

    // Add district boundaries if we have them
    if (geoData && geoData.features) {
      const geoLayer = L.geoJSON(geoData, {
        style: (feature) => {
          const cd = parseInt(feature.properties.BASENAME || feature.properties.CD119 || "0", 10);
          const colorIdx = (cd - 1) % DISTRICT_COLORS.length;
          return {
            fillColor: DISTRICT_COLORS[colorIdx >= 0 ? colorIdx : 0],
            fillOpacity: 0.35,
            color: "#374151",
            weight: 2,
            opacity: 0.8,
          };
        },
        onEachFeature: (feature, layer) => {
          const cd = feature.properties.BASENAME || feature.properties.CD119 || "1";
          const cdNum = parseInt(cd, 10);
          const isAtLarge = districts === 1;
          const label = isAtLarge ? `${stateName} At-Large` : `${stateName} District ${cdNum}`;

          // Tooltip on hover
          layer.bindTooltip(label, {
            sticky: true,
            className: "district-tooltip",
            direction: "top",
            offset: [0, -10],
          });

          // Click to select district
          layer.on("click", () => {
            if (onDistrictClick) {
              onDistrictClick(cdNum <= 0 ? 1 : cdNum, label);
            }
          });

          // Hover effects
          layer.on("mouseover", () => {
            layer.setStyle({ fillOpacity: 0.6, weight: 3 });
            layer.bringToFront();
          });
          layer.on("mouseout", () => {
            layer.setStyle({ fillOpacity: 0.35, weight: 2 });
          });
        },
      }).addTo(map);

      // Fit map to district boundaries
      try {
        map.fitBounds(geoLayer.getBounds(), { padding: [20, 20] });
      } catch (e) { /* ignore if bounds are invalid */ }
    }

    // Cleanup on unmount
    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, [geoData, stateAbbr, stateName, districts, onDistrictClick]);

  return (
    <div style={{ marginBottom: 24 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
        <h3 style={{ fontSize: 13, fontWeight: 700, color: "#8B8FA3", letterSpacing: 1.5, textTransform: "uppercase", fontFamily: F, margin: 0 }}>
          District Map
        </h3>
        {loading && (
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <div style={{ width: 14, height: 14, border: "2px solid #E8ECF1", borderTop: "2px solid #4ECDC4", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
            <span style={{ fontSize: 11, color: "#8B8FA3", fontFamily: F }}>Loading boundaries...</span>
          </div>
        )}
      </div>
      {error && (
        <div style={{ padding: "12px 16px", borderRadius: 10, background: "#FFF8E1", border: "1px solid #F59E0B33", fontSize: 12, color: "#92400E", fontFamily: F, marginBottom: 12 }}>
          ⚠️ {error} — use the list below to select a district.
        </div>
      )}
      <div style={{ borderRadius: 16, overflow: "hidden", border: "1px solid #E8ECF1", boxShadow: "0 2px 8px rgba(0,0,0,0.04)" }}>
        <div ref={mapRef} style={{ height: 420, width: "100%", background: "#F0F1F3" }} />
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 8 }}>
        <span style={{ fontSize: 11, color: "#9CA3AF", fontFamily: F }}>Click a district on the map to view candidates, or choose from the list below</span>
      </div>
      <style>{`
        .district-tooltip {
          font-family: ${F};
          font-size: 13px;
          font-weight: 600;
          padding: 6px 12px;
          border-radius: 8px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.15);
          border: none;
        }
      `}</style>
    </div>
  );
}

function ElectionDates({ districtId }) {
  const seed = districtId.split('').reduce((a, c) => a + c.charCodeAt(0), 0);
  const r = rng(seed * 31); const mo = Math.floor(r() * 5) + 3; const day = Math.floor(r() * 20) + 5;
  const months = ["","Jan","Feb","March","April","May","June","July","Aug","Sep","Oct","Nov","Dec"];
  return (
    <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginBottom: 20 }}>
      <div style={{ flex: 1, minWidth: 200, background: "linear-gradient(135deg, #667EEA22, #764BA222)", borderRadius: 12, padding: "14px 18px", border: "1px solid #667EEA33" }}>
        <div style={{ fontSize: 10, color: "#667EEA", fontWeight: 700, fontFamily: F, letterSpacing: 1.5, textTransform: "uppercase", marginBottom: 4 }}>Primary Election</div>
        <div style={{ fontSize: 16, fontWeight: 700, color: "#374151", fontFamily: PF }}>{months[mo]} {day}, 2026</div>
      </div>
      <div style={{ flex: 1, minWidth: 200, background: "linear-gradient(135deg, #4ECDC422, #45B7D122)", borderRadius: 12, padding: "14px 18px", border: "1px solid #4ECDC433" }}>
        <div style={{ fontSize: 10, color: "#2DA89E", fontWeight: 700, fontFamily: F, letterSpacing: 1.5, textTransform: "uppercase", marginBottom: 4 }}>General Election</div>
        <div style={{ fontSize: 16, fontWeight: 700, color: "#374151", fontFamily: PF }}>November 3, 2026</div>
      </div>
    </div>
  );
}

function ApiKeyPanel({ apiKey, setApiKey, onClose }) {
  const [val, setVal] = useState(apiKey);
  const [testing, setTesting] = useState(false);
  const [result, setResult] = useState(null);
  const test = async () => {
    setTesting(true); setResult(null);
    try {
      const r = await fetch(`${FEC_BASE}/candidates/search/?api_key=${val}&state=CA&office=H&per_page=1`);
      setResult(r.ok ? { ok: true, msg: "API key is valid!" } : { ok: false, msg: `Error: ${r.status}` });
    } catch { setResult({ ok: false, msg: "Network error" }); }
    setTesting(false);
  };
  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(0,0,0,0.4)", backdropFilter: "blur(4px)" }} onClick={onClose}>
      <div style={{ background: "#fff", borderRadius: 20, padding: 32, maxWidth: 480, width: "90%", boxShadow: "0 20px 60px rgba(0,0,0,0.15)", animation: "fadeIn 0.3s ease" }} onClick={e => e.stopPropagation()}>
        <h2 style={{ fontSize: 22, fontWeight: 700, color: "#1A1D2E", fontFamily: PF, margin: "0 0 8px" }}>FEC API Configuration</h2>
        <p style={{ fontSize: 13, color: "#6B7280", lineHeight: 1.6, margin: "0 0 20px", fontFamily: F }}>Enter your free FEC API key to load real campaign finance data. Without a key, the app shows simulated data.</p>
        <a href="https://api.open.fec.gov/developers/" target="_blank" rel="noopener noreferrer" style={{ fontSize: 13, color: "#4ECDC4", fontWeight: 600, fontFamily: F, textDecoration: "none", display: "block", marginBottom: 16 }}>→ Get a free API key at api.open.fec.gov</a>
        <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
          <input type="text" placeholder="Paste your FEC API key..." value={val} onChange={e => { setVal(e.target.value.trim()); setResult(null); }}
            style={{ flex: 1, padding: "12px 16px", border: "1px solid #E8ECF1", borderRadius: 10, fontSize: 14, fontFamily: F, outline: "none", color: "#1A1D2E" }} />
          <button onClick={test} disabled={testing || !val} style={{ padding: "12px 18px", borderRadius: 10, border: "none", background: val ? "#F0F1F3" : "#F8F9FB",
            color: val ? "#374151" : "#CBD5E1", fontWeight: 600, fontSize: 13, cursor: val ? "pointer" : "default", fontFamily: F }}>{testing ? "Testing..." : "Test"}</button>
        </div>
        {result && <div style={{ padding: "10px 14px", borderRadius: 8, marginBottom: 12, background: result.ok ? "#E8F8F5" : "#FDEDEC", color: result.ok ? "#27AE60" : "#E74C3C", fontSize: 13, fontFamily: F }}>{result.ok ? "✓" : "✕"} {result.msg}</div>}
        <div style={{ display: "flex", gap: 10, marginTop: 20 }}>
          <button onClick={() => { setApiKey(val); onClose(); }} style={{ flex: 1, padding: 12, borderRadius: 10, border: "none", background: "linear-gradient(135deg, #4ECDC4, #45B7D1)", color: "#fff", fontWeight: 700, fontSize: 14, cursor: "pointer", fontFamily: F }}>Save Key</button>
          <button onClick={() => { setVal(""); setApiKey(""); setResult(null); }} style={{ padding: "12px 20px", borderRadius: 10, border: "1px solid #E8ECF1", background: "#fff", color: "#6B7280", fontWeight: 600, fontSize: 13, cursor: "pointer", fontFamily: F }}>Clear</button>
        </div>
        {!apiKey && <p style={{ fontSize: 11, color: "#9CA3AF", fontStyle: "italic", marginTop: 14, textAlign: "center", fontFamily: F }}>Without an API key, the app works with simulated demo data</p>}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// MAIN APP
// ═══════════════════════════════════════════════════════════════════════════
export default function ElectionTracker() {
  const [view, setView] = useState("home");
  const [selState, setSelState] = useState(null);
  const [selElection, setSelElection] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [zip, setZip] = useState("");
  const [zipErr, setZipErr] = useState("");
  const [focused, setFocused] = useState(false);
  const [apiKey, setApiKey] = useState(process.env.REACT_APP_FEC_API_KEY || "");
  const envKeyPresent = !!process.env.REACT_APP_FEC_API_KEY;
  const [showApi, setShowApi] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadErr, setLoadErr] = useState(null);

  const selectElection = useCallback(async (id, label, stateAbbr, district, office) => {
    setSelElection({ id, label }); setView("election"); setLoading(true); setLoadErr(null); setCandidates([]);
    if (apiKey) {
      try {
        const res = await fetchRaceData(apiKey, stateAbbr, district, office);
        if (res.candidates.length > 0) {
          setCandidates(res.candidates); setLoading(false);
          if (res.year && res.year !== 2026) setLoadErr(`No 2026 filings yet — showing ${res.year} cycle data from FEC.`);
          return;
        }
        setLoadErr("No FEC candidates found for this race. Showing simulated data.");
      } catch (err) { setLoadErr(`FEC API: ${err.message}. Showing simulated data.`); }
    }
    setCandidates(generateMockCandidates(id)); setLoading(false);
  }, [apiKey]);

  const searchZip = useCallback(async () => {
    setZipErr("");
    if (!/^\d{5}$/.test(zip)) { setZipErr("Enter a valid 5-digit zip code"); return; }

    // Show loading state
    setLoading(true); setView("election"); setSelElection({ id: "loading", label: "Looking up your district..." }); setCandidates([]);

    // Try Census geocoder first for accurate district resolution
    const censusResult = await geocodeZipToDistrict(zip);
    if (censusResult) {
      const state = US_STATES.find(s => s.abbr === censusResult.stateAbbr);
      if (state) {
        const dist = Math.min(censusResult.district, state.districts) || 1;
        setSelState(state);
        const label = state.districts === 1
          ? `${state.name} At-Large District (House)`
          : `${state.name} District ${dist} (House)`;
        await selectElection(`${state.abbr}-${dist}`, label, state.abbr, dist, "H");
        return;
      }
    }

    // Fallback to simple zip prefix table
    const r = zipToDistrict(zip);
    if (!r) { setZipErr("Could not find a district for this zip code"); setView("home"); setLoading(false); return; }
    setSelState(r.state);
    await selectElection(`${r.state.abbr}-${r.district}`, `${r.state.name} District ${r.district} (House)`, r.state.abbr, r.district, "H");
  }, [zip, selectElection]);

  const back = useCallback(() => {
    if (view === "election") { setLoadErr(null); selState ? setView("state") : setView("home"); }
    else { setView("home"); setSelState(null); }
  }, [view, selState]);

  const inc = candidates.find(c => c.isIncumbent);
  const COLORS = ["#4ECDC4","#667EEA","#F7DC6F","#E8A87C","#C38D9E","#85CDCA"];

  return (
    <div style={{ minHeight: "100vh", background: "#FAFBFC", fontFamily: F }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;600;700;800&family=DM+Sans:wght@300;400;500;600;700&display=swap');
        @keyframes fadeIn { from { opacity:0; transform:translateY(12px) } to { opacity:1; transform:translateY(0) } }
        @keyframes spin { to { transform: rotate(360deg) } }
        * { box-sizing: border-box } body { margin: 0 }
      `}</style>

      {showApi && !envKeyPresent && <ApiKeyPanel apiKey={apiKey} setApiKey={setApiKey} onClose={() => setShowApi(false)} />}

      <header style={{ background: "rgba(255,255,255,0.92)", borderBottom: "1px solid #E8ECF1", padding: "0 24px", position: "sticky", top: 0, zIndex: 100, backdropFilter: "blur(12px)" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto", display: "flex", alignItems: "center", justifyContent: "space-between", height: 64 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer" }} onClick={() => { setView("home"); setSelState(null); setSelElection(null); setLoadErr(null); }}>
            <div style={{ width: 36, height: 36, borderRadius: 10, background: "linear-gradient(135deg, #4ECDC4, #45B7D1)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18 }}>🗳</div>
            <div><div style={{ fontSize: 16, fontWeight: 700, color: "#1A1D2E", fontFamily: PF, lineHeight: 1.1 }}>Election Tracker</div>
            <div style={{ fontSize: 10, color: "#8B8FA3", letterSpacing: 1.5, textTransform: "uppercase" }}>US Congress 2026</div></div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            {view !== "home" && <button onClick={back} style={{ background: "#F0F1F3", border: "none", borderRadius: 8, padding: "8px 16px", cursor: "pointer", fontSize: 13, fontWeight: 600, color: "#374151", fontFamily: F }}>← Back</button>}
            {envKeyPresent ? (
              <span style={{ background: "#E8F8F5", border: "1px solid #4ECDC444",
                borderRadius: 8, padding: "8px 14px", fontSize: 12, fontWeight: 600, color: "#27AE60", fontFamily: F, display: "flex", alignItems: "center", gap: 5 }}>
                <span style={{ width: 7, height: 7, borderRadius: "50%", background: "#27AE60" }} />FEC Connected
              </span>
            ) : (
            <button onClick={() => setShowApi(true)} style={{ background: apiKey ? "#E8F8F5" : "#FFF8E1", border: `1px solid ${apiKey ? "#4ECDC444" : "#F59E0B44"}`,
              borderRadius: 8, padding: "8px 14px", cursor: "pointer", fontSize: 12, fontWeight: 600, color: apiKey ? "#27AE60" : "#F59E0B", fontFamily: F, display: "flex", alignItems: "center", gap: 5 }}>
              <span style={{ width: 7, height: 7, borderRadius: "50%", background: apiKey ? "#27AE60" : "#F59E0B" }} />{apiKey ? "FEC Connected" : "Add API Key"}
            </button>
            )}
          </div>
        </div>
      </header>

      <main style={{ maxWidth: 1100, margin: "0 auto", padding: "24px 24px 60px" }}>

        {view === "home" && (
          <div style={{ animation: "fadeIn 0.4s ease" }}>
            <div style={{ textAlign: "center", marginBottom: 32 }}>
              <h1 style={{ fontSize: 36, fontWeight: 800, color: "#1A1D2E", fontFamily: PF, margin: "0 0 8px" }}>Track Your Elections</h1>
              <p style={{ fontSize: 16, color: "#6B7280", maxWidth: 520, margin: "0 auto", lineHeight: 1.6 }}>Search by zip code or click a state on the map to explore House and Senate races.</p>
              {!apiKey && !envKeyPresent && <button onClick={() => setShowApi(true)} style={{ marginTop: 12, padding: "8px 20px", borderRadius: 20, border: "1px dashed #F59E0B", background: "#FFF8E1", color: "#92400E", fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: F }}>⚡ Add your free FEC API key for real campaign finance data</button>}
            </div>
            <div style={{ maxWidth: 480, margin: "0 auto 36px", display: "flex", gap: 8 }}>
              <div style={{ flex: 1, borderRadius: 12, boxShadow: focused ? "0 0 0 3px #4ECDC444" : "0 2px 8px rgba(0,0,0,0.04)", transition: "box-shadow 0.2s" }}>
                <input type="text" placeholder="Enter your zip code..." value={zip} onChange={e => { setZip(e.target.value.replace(/\D/g,'').substring(0,5)); setZipErr(""); }}
                  onFocus={() => setFocused(true)} onBlur={() => setFocused(false)} onKeyDown={e => e.key === "Enter" && searchZip()}
                  style={{ width: "100%", padding: "14px 18px", border: "1px solid #E8ECF1", borderRadius: 12, fontSize: 15, fontFamily: F, outline: "none", background: "#fff", color: "#1A1D2E" }} />
              </div>
              <button onClick={searchZip} style={{ background: "linear-gradient(135deg, #4ECDC4, #45B7D1)", border: "none", borderRadius: 12, padding: "14px 24px", color: "#fff", fontWeight: 700, fontSize: 14, fontFamily: F, cursor: "pointer", boxShadow: "0 4px 14px #4ECDC444", whiteSpace: "nowrap" }}>Search</button>
            </div>
            {zipErr && <div style={{ textAlign: "center", color: "#E74C3C", fontSize: 13, marginTop: -24, marginBottom: 16, fontFamily: F }}>{zipErr}</div>}
            <div style={{ position: "relative" }}>
              <div style={{ textAlign: "center", marginBottom: 12 }}><span style={{ fontSize: 12, color: "#8B8FA3", fontFamily: F }}>Click on a state to view its races</span></div>
              <USLeafletMap onStateClick={s => { setSelState(s); setView("state"); }} selectedState={selState?.abbr} />
            </div>
          </div>
        )}

        {view === "state" && selState && (
          <div style={{ animation: "fadeIn 0.4s ease" }}>
            <h2 style={{ fontSize: 28, fontWeight: 800, color: "#1A1D2E", fontFamily: PF, margin: "0 0 4px" }}>{selState.name}</h2>
            <p style={{ fontSize: 14, color: "#6B7280", margin: "0 0 20px" }}>{selState.districts} House {selState.districts === 1 ? "seat" : "seats"}{SENATE_RACES_2026.has(selState.abbr) ? " · 1 Senate race" : ""}</p>

            <DistrictMap
              stateAbbr={selState.abbr}
              stateName={selState.name}
              districts={selState.districts}
              onDistrictClick={(distNum, label) => {
                const atLarge = selState.districts === 1;
                const elLabel = atLarge
                  ? `${selState.name} At-Large District (House)`
                  : `${selState.name} District ${distNum} (House)`;
                selectElection(`${selState.abbr}-${distNum}`, elLabel, selState.abbr, distNum, "H");
              }}
            />

            {SENATE_RACES_2026.has(selState.abbr) && (<>
            <h3 style={{ fontSize: 13, fontWeight: 700, color: "#8B8FA3", letterSpacing: 1.5, textTransform: "uppercase", fontFamily: F, marginBottom: 12 }}>Senate Race</h3>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 10, marginBottom: 28 }}>
              <button onClick={() => selectElection(`${selState.abbr}-SEN`, `${selState.name} U.S. Senate Race`, selState.abbr, null, "S")}
                style={{ background: "#fff", border: "1px solid #E8ECF1", borderRadius: 14, padding: "18px 20px", cursor: "pointer", textAlign: "left", transition: "all 0.2s", fontFamily: F }}
                onMouseEnter={e => { e.currentTarget.style.borderColor="#4ECDC4"; e.currentTarget.style.boxShadow="0 4px 16px #4ECDC422"; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor="#E8ECF1"; e.currentTarget.style.boxShadow="none"; }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <div style={{ width: 42, height: 42, borderRadius: 10, background: "linear-gradient(135deg, #667EEA22, #764BA222)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18 }}>🏛</div>
                  <div><div style={{ fontSize: 15, fontWeight: 700, color: "#1A1D2E" }}>U.S. Senate</div><div style={{ fontSize: 12, color: "#8B8FA3" }}>2026 Senate Race · {selState.name}</div></div>
                  <div style={{ marginLeft: "auto", fontSize: 18, color: "#CBD5E1" }}>→</div>
                </div>
              </button>
            </div>
            </>)}

            <h3 style={{ fontSize: 13, fontWeight: 700, color: "#8B8FA3", letterSpacing: 1.5, textTransform: "uppercase", fontFamily: F, marginBottom: 12 }}>House Districts</h3>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 10 }}>
              {Array.from({ length: selState.districts }, (_, i) => {
                const d = i + 1; const atLarge = selState.districts === 1;
                return (
                  <button key={d} onClick={() => selectElection(`${selState.abbr}-${d}`, `${selState.name} ${atLarge?"At-Large":"District "+d} (House)`, selState.abbr, d, "H")}
                    style={{ background: "#fff", border: "1px solid #E8ECF1", borderRadius: 14, padding: "18px 20px", cursor: "pointer", textAlign: "left", transition: "all 0.2s", fontFamily: F }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor="#4ECDC4"; e.currentTarget.style.boxShadow="0 4px 16px #4ECDC422"; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor="#E8ECF1"; e.currentTarget.style.boxShadow="none"; }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                      <div style={{ width: 42, height: 42, borderRadius: 10, background: "linear-gradient(135deg, #4ECDC422, #45B7D122)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16, fontWeight: 700, color: "#2DA89E", fontFamily: F }}>{d}</div>
                      <div><div style={{ fontSize: 15, fontWeight: 700, color: "#1A1D2E" }}>{atLarge ? "At-Large" : `District ${d}`}</div><div style={{ fontSize: 12, color: "#8B8FA3" }}>U.S. House · {selState.name}</div></div>
                      <div style={{ marginLeft: "auto", fontSize: 18, color: "#CBD5E1" }}>→</div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {view === "election" && selElection && (
          <div style={{ animation: "fadeIn 0.4s ease" }}>
            <h2 style={{ fontSize: 26, fontWeight: 800, color: "#1A1D2E", fontFamily: PF, margin: "0 0 4px" }}>{selElection.label}</h2>
            {!loading && candidates.length > 0 && (
              <p style={{ fontSize: 14, color: "#6B7280", margin: "0 0 4px" }}>
                {candidates.length} candidates · {inc ? `Incumbent: ${inc.name}${inc.yearsHeld > 0 ? ` (${inc.yearsHeld} years)` : ""}` : "Open seat — no incumbent"}
              </p>
            )}
            {loadErr && (
              <div style={{ padding: "12px 18px", borderRadius: 12, margin: "12px 0", background: "#FFF8E1", border: "1px solid #F59E0B33", fontSize: 13, color: "#92400E", fontFamily: F, display: "flex", alignItems: "center", gap: 8 }}>
                ⚠️ {loadErr}
              </div>
            )}
            {loading ? (
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: "60px 20px", gap: 16 }}>
                <div style={{ width: 40, height: 40, border: "4px solid #E8ECF1", borderTop: "4px solid #4ECDC4", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
                <p style={{ fontSize: 14, color: "#6B7280", fontFamily: F }}>{apiKey ? "Fetching from FEC API..." : "Loading..."}</p>
              </div>
            ) : (
              <>
                <ElectionDates districtId={selElection.id} />
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(380px, 1fr))", gap: 20 }}>
                  {candidates.map((c, i) => <CandidateCard key={c.id} candidate={c} index={i} />)}
                </div>
              </>
            )}
          </div>
        )}
      </main>

      <footer style={{ borderTop: "1px solid #E8ECF1", padding: "20px 24px", textAlign: "center", background: "#fff" }}>
        <p style={{ fontSize: 12, color: "#9CA3AF", fontFamily: F, margin: 0 }}>Election Tracker 2026 · Campaign finance data from the Federal Election Commission · Not affiliated with any political organization</p>
      </footer>
    </div>
  );
}
